import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMe, getLatestVoteRound, createVoteRound, submitVoteBallots, cancelVoteRound,
  submitVoteNominations, voteRoundRegistry, removeVoteVoter, addVoteVoters, staffReachable,
  setVoteCandidateAvailability, addVoteCandidates, advanceVoteRound, nudgeVoteRound,
  listRegistryLaborCos, listStaff, ROLE_LABEL, VOTE_STATUS_LABEL, VOTE_PHASE_LABEL, VOTE_ACCEPTANCE_LABEL,
  VOTE_AVAILABILITY_LABEL,
  type MeResponse, type RegistryLaborCo, type StaffRow, type ApiError,
  type VoteRoundView, type VoteCandidate, type VoteVoter, type VoteBallot, type BallotInput,
  type VotePool, type VotePoolCandidate,
  type VoteAcceptanceOutcome, type VoteCandidatePastPackage, type VoteRegistryCo, type NominationInput,
  type VoteAvailability,
} from '../lib/api';
import { toast } from './ui/Toast';

/**
 * 定标投票 / recommendation vote (2026-07 meeting, explainer §6).
 *
 * The WeChat-group "谁推荐这家" thread, moved in-system and held BEFORE 邀请劳务. The panel must carry
 * at least one 商务 and one 工程; every listed voter must vote before anyone can be invited
 * ("5张票必须投完，否则进行不下去"). 反对 needs a reason and shows OPENLY here, before bidding — it does
 * not remove a candidate; the round's creator (or the GM) decides what to do with it.
 *
 * TWO PHASES (改改 fb_85f73594). 推荐: the panel browses the whole supplier registry and recommends
 * (an empty submission = 无补充); when the last member submits, the union becomes the slate and the
 * round flips to 表决 — the ballot form below, unchanged.
 *
 * Price-free by construction — the same payload feeds 我的任务, where 项目经理 acts.
 */

const errText = (e: unknown, fallback = '操作失败 / failed') =>
  (e as ApiError)?.body?.detail ?? (e as ApiError)?.body?.error ?? (e as Error)?.message ?? fallback;

/**
 * Never dead-end on a greyed-out button (house lesson, 更换项目经理 picker 7/17): the submit buttons here
 * stay CLICKABLE and, when a requirement is unmet, say exactly which one — and where to go fix it —
 * at the moment of the click. The small inline hints stay as the ambient version of the same thing.
 */
function toastGaps(gaps: string[]) {
  toast(<span>{gaps.map((g, i) => <div key={i}>{g}</div>)}</span>, { kind: 'error', duration: 7000 });
}

/**
 * A server timestamp in the READER's clock. Every stamp on this panel is stored UTC ISO, so slicing the
 * string ('2026-08-17T01:30') shows a Beijing reader a time eight hours out of date — 「上次催办」 and the
 * 联系结果 stamp are both things people compare against "now", so both go through here.
 * `time` gives the bare HH:mm the chips use; the default gives the full date the tooltips want.
 */
function fmtLocal(ts: string | null | undefined, mode: 'datetime' | 'time' = 'datetime'): string {
  if (!ts) return '';
  const d = new Date(ts);
  // an unparseable stamp shows its raw self rather than 'Invalid Date'
  if (Number.isNaN(d.getTime())) return ts.slice(0, 16).replace('T', ' ');
  return mode === 'time'
    ? d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Ballot tallies + who said what, per candidate. */
function ballotsFor(v: VoteRoundView, candidateId: string): VoteBallot[] {
  return v.ballots.filter((b) => b.candidate_id === candidateId);
}

/** Compact per-package 验收 tag — the full bilingual wording rides in the row's title. */
const ACCEPTANCE_MARK: Record<VoteAcceptanceOutcome, string> = { passed: '✓验收', escalated: '⚠整改升级', in_progress: '…验收中' };

/** One past package, spelled out for the tooltip: 中标/验收/终验 in both languages. */
function pastPackageTitle(p: VoteCandidatePastPackage): string {
  const acc = p.acceptance ? VOTE_ACCEPTANCE_LABEL[p.acceptance]?.label ?? p.acceptance : '未验收 / not yet accepted';
  const fin = p.final_inspection === 'passed' ? ' · 终验通过 / final inspection passed' : '';
  return `${p.name}（${p.status} · ${acc}${fin}）`;
}

/**
 * The candidate's track record. Beyond 往来/中标 the panel now carries the ACCEPTANCE record the
 * meeting asked for — the PM's 验收 and the GM's 终验 per past package — so a voter judges delivery,
 * not just history. Counts + names + statuses only: this renders on the PM's price-free surface.
 */
function HistoryChips({ c }: { c: { history: VoteCandidate['history'] } }) {
  const h = c.history;
  // F5: PMs vote on a PHONE, where title= tooltips do not exist. The per-package 验收/终验 detail is a
  // TAP away — the summary line is a real button and the full bilingual record expands inline beneath it.
  const [open, setOpen] = useState(false);
  if (!h || h.past_engagements === 0) return <span className="pill pill-muted small">新供应商 / first time with us</span>;
  return (
    <span className="row-wrap small" style={{ gap: 6, alignItems: 'baseline' }}>
      <span className="pill pill-stage small">往来 {h.past_engagements} 次 / engagements</span>
      <span className={`pill small ${h.awarded ? 'pill-good' : 'pill-muted'}`}>中标 {h.awarded} 次 / awarded</span>
      {h.acceptance_passed > 0 && (
        <span className="pill pill-good small" title="项目经理验收通过的采购包 / packages the PM accepted">验收通过 {h.acceptance_passed} / accepted</span>
      )}
      {h.acceptance_escalated > 0 && (
        <span className="pill pill-warn small" title="整改未闭环、已升级处理 / acceptance escalated after remediation">验收整改升级 {h.acceptance_escalated} / escalated</span>
      )}
      {h.final_passed > 0 && (
        <span className="pill pill-good small" title="总经理终验通过 / GM final inspection passed">终验通过 {h.final_passed} / final</span>
      )}
      {/* stopPropagation: the nomination picker renders these chips INSIDE its row <label> — without it
          a browser that activates the label from an interactive descendant would nominate on a read. */}
      <button className="small chip-disclose"
        aria-expanded={open} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}>
        {open ? '▾' : '▸'}{' '}
        {h.packages.slice(0, 4).map((p, i) => (
          <span key={`${p.name}-${i}`}>
            {i > 0 ? '、' : ''}{p.name}
            {p.acceptance && (
              <span className={`pill small ${VOTE_ACCEPTANCE_LABEL[p.acceptance]?.pill ?? 'pill-muted'}`} style={{ marginLeft: 3 }}>
                {ACCEPTANCE_MARK[p.acceptance] ?? p.acceptance}
              </span>
            )}
            {p.final_inspection === 'passed' && <span className="pill pill-good small" style={{ marginLeft: 3 }}>终验</span>}
          </span>
        ))}
        {h.packages.length > 4 ? ` …+${h.packages.length - 4}` : ''}
      </button>
      {open && (
        <div className="small muted" style={{ flexBasis: '100%', paddingLeft: 12, borderLeft: '2px solid var(--rule)', marginTop: 2 }}>
          {h.packages.map((p, i) => <div key={`${p.name}-detail-${i}`}>· {pastPackageTitle(p)}</div>)}
        </div>
      )}
    </span>
  );
}

/** The three 联系结果 buttons, in the order 预算 actually uses them after a phone call. */
const AVAILABILITY_ACTIONS: { value: VoteAvailability; label: string }[] = [
  { value: 'available', label: '✓ 可参与' },
  { value: 'unavailable', label: '✗ 不可参与' },
  { value: 'unknown', label: '↺ 未联系' },
];

/**
 * One candidate: identity + 联系结果 + track record + the open tally + every ballot's verdict and note.
 *
 * 改改 fb_828ed0af: 预算 phones the recommended companies and only one of four has time. Recording that
 * HERE takes the company out of everyone's ballot (`不可参与` — the row stays, greyed, so nobody wonders
 * where it went) instead of sending the whole panel back to recommend from scratch. `canMark` is the
 * 估价/采购/GM surface; the PM reads the result on their own price-free page and never marks anything.
 */
export function CandidateRow({ v, c, canMark = false, onChanged }: {
  v: VoteRoundView; c: VoteCandidate; canMark?: boolean; onChanged?: () => void;
}) {
  const [busy, setBusy] = useState<VoteAvailability | null>(null);
  const bs = ballotsFor(v, c.id);
  const approve = bs.filter((b) => b.verdict === 'approve').length;
  const objections = bs.filter((b) => b.verdict === 'object');
  const av: VoteAvailability = c.availability ?? 'unknown';
  const out = av === 'unavailable';
  const rid = v.round?.id;

  async function mark(next: VoteAvailability) {
    if (!rid || next === busy) return;
    let note: string | undefined;
    if (next === 'unavailable') {
      // the reason is the whole value of the record a week later ("说没人手" vs "报价太低不接")
      const r = prompt(`「${c.labor_co_name}」不可参与的原因？（可不填）\n记录后本轮不再需要对其表决，其他候选的票保留。\n/ Why can't they take part? (optional) — they drop out of everyone's ballot; other verdicts stand.`);
      if (r === null) return;
      note = r.trim() || undefined;
    }
    setBusy(next);
    try {
      await setVoteCandidateAvailability(rid, c.id, { availability: next, ...(note ? { note } : {}) });
      toast(`已记录「${c.labor_co_name}」：${VOTE_AVAILABILITY_LABEL[next].label}`, { kind: 'success' });
      onChanged?.();
    } catch (e) { toast(errText(e, '记录联系结果失败 / could not record the contact result'), { kind: 'error', duration: 7000 }); }
    finally { setBusy(null); }
  }

  return (
    <div style={{ padding: '8px 0', borderTop: '1px solid var(--rule)', opacity: out ? 0.55 : 1 }}>
      <div className="row-wrap vote-row" style={{ gap: 8, alignItems: 'baseline' }}>
        <strong>{c.labor_co_name}</strong>
        <span className={`pill small ${VOTE_AVAILABILITY_LABEL[av].pill}`}>{VOTE_AVAILABILITY_LABEL[av].label}</span>
        {out && <span className="small muted">已排除表决 / dropped from ballots</span>}
        <span className={`pill small ${approve ? 'pill-good' : 'pill-muted'}`}>同意 {approve}</span>
        <span className={`pill small ${objections.length ? 'pill-bad' : 'pill-muted'}`}>反对 {objections.length}</span>
        <span className="spacer" />
        <HistoryChips c={c} />
      </div>
      {canMark && rid && (
        <div className="row-wrap small" style={{ gap: 6, marginTop: 4 }}>
          <span className="muted">联系结果 / contact result：</span>
          {AVAILABILITY_ACTIONS.map((a) => (
            <button key={a.value} className={av === a.value ? 'btn-primary small' : 'small'} disabled={!!busy}
              onClick={() => mark(a.value)}
              title={`记录「${c.labor_co_name}」的联系结果 / record what they said`}>
              {busy === a.value ? '记录中…' : a.label}
            </button>
          ))}
        </div>
      )}
      {(c.availability_by || c.availability_note) && (
        <div className="small muted" style={{ marginTop: 2 }}>
          {c.availability_by ?? '—'}{c.availability_at ? ` · ${fmtLocal(c.availability_at)}` : ''}
          {c.availability_note ? <span style={{ color: 'var(--ink)' }}> — {c.availability_note}</span> : null}
        </div>
      )}
      {bs.length > 0 && (
        <div className="small" style={{ marginTop: 4, paddingLeft: 2 }}>
          {bs.map((b) => (
            <div key={b.id} style={{ color: out ? 'var(--muted)' : (b.verdict === 'object' ? 'var(--bad)' : 'var(--muted)') }}>
              {b.verdict === 'object' ? '✗ 反对' : '✓ 同意'} · {b.staff_name ?? '—'}
              {b.note ? <span style={{ color: out ? 'var(--muted)' : 'var(--ink)' }}> — {b.note}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const UNREACHABLE_HINT = '未绑定企业微信且从未登录 —— 可能收不到通知、无法投票；请移除或让总经理帮其登录 / not bound to WeCom and never signed in — may never receive the notification or be able to vote';

/**
 * The panel, at a glance — and the answer to "why has this round been open for a week?".
 *
 * A voter who is neither 企业微信-bound nor has ever signed in wears a ⚠: they may never see the round
 * at all, and an open round blocks 邀请劳务 forever (blocking finding (b)). The creator/GM can drop
 * them from here (✕) — the server re-checks the 商务+工程 quorum and closes the round if they were the
 * last one holding it up.
 */
export function VoterChips({ voters, phase = 'voting', roundId, canManage = false, onChanged }: {
  voters: VoteVoter[]; phase?: 'nominating' | 'voting'; roundId?: string; canManage?: boolean; onChanged?: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const acted = (v: VoteVoter) => (phase === 'nominating' ? !!v.nominated_at : !!v.voted_at);
  const actedLabel = phase === 'nominating' ? '已提交推荐 / recommended' : '已投票 / voted';
  const pendingLabel = phase === 'nominating' ? '尚未推荐 / not yet recommended' : '尚未投票 / not voted yet';

  async function remove(v: VoteVoter) {
    if (!roundId) return;
    const reason = prompt(`将「${v.staff_name}」移出投票组？\n本人已投的票会一并删除，且商务/工程各一人的要求仍需满足。\n/ Remove ${v.staff_name} from the panel? Their ballots go with them.\n\n原因（可不填）/ reason (optional):`);
    if (reason === null) return;
    setBusy(v.id);
    try {
      await removeVoteVoter(roundId, v.id, reason.trim() || undefined);
      toast(`已将「${v.staff_name}」移出投票组 / removed from the panel`, { kind: 'success' });
      onChanged?.();
    } catch (e) { toast(errText(e, '移除失败 / could not remove'), { kind: 'error', duration: 7000 }); }
    finally { setBusy(null); }
  }

  return (
    <span className="row-wrap small" style={{ gap: 6 }}>
      {voters.map((v) => (
        <span key={v.id} className={`pill small ${acted(v) ? 'pill-good' : (v.reachable === false ? 'pill-bad' : 'pill-warn')}`}
          title={`${ROLE_LABEL[v.role] ?? v.role} · ${acted(v) ? actedLabel : pendingLabel}`
            // 提前进入表决 stamped them — the ✓ must not read as "they submitted" (改改 fb_828ed0af).
            // 推荐 only: the flag describes their NOMINATION, so hanging it off a 表决 chip
            // ("已投票（发起人跳过·无补充）") would deny a ballot they really did cast.
            + `${phase === 'nominating' && v.nominated_forced ? '（发起人跳过·无补充）/ skipped by the creator — recorded as no additions' : ''}`
            + `${v.reachable === false ? ` · ${UNREACHABLE_HINT}` : ''}`}>
          {acted(v) ? '✓' : '…'} {v.staff_name}
          {v.reachable === false && <span title={UNREACHABLE_HINT}> ⚠</span>}
          {canManage && roundId && !!onChanged && (
            <button className="small chip-remove" disabled={busy === v.id} onClick={() => remove(v)}
              title={`将「${v.staff_name}」移出投票组 / remove from the panel`}>✕</button>
          )}
        </span>
      ))}
      {canManage && roundId && !!onChanged && (
        <button className="small" onClick={() => setAdding(true)}
          title="把同事加入本轮投票组 / add a colleague to this panel">＋ 添加成员 / add</button>
      )}
      {adding && roundId && (
        <AddVoterPicker roundId={roundId} phase={phase} current={voters}
          onClose={() => setAdding(false)} onAdded={() => { setAdding(false); onChanged?.(); }} />
      )}
    </span>
  );
}

/**
 * The mid-round 添加成员 picker (改改 8/10). Deliberately the SAME roster surface as StartVoteForm's
 * panel column — /staff already returns ACTIVE non-placeholder rows only, and unreachable people sort
 * to the bottom wearing their ⚠ rather than being hidden: adding someone who will never see the round
 * is how a round hangs, so it stays possible but never the easy default.
 *
 * People already on the panel are listed as disabled+checked rather than filtered out, so the picker
 * reads as the whole panel roster instead of a mystery subset.
 */
function AddVoterPicker({ roundId, phase, current, onClose, onAdded }: {
  roundId: string; phase: 'nominating' | 'voting'; current: VoteVoter[]; onClose: () => void; onAdded: () => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const onPanel = useMemo(() => new Set(current.map((v) => v.staff_id).filter(Boolean) as string[]), [current]);

  // same query as StartVoteForm — refetch on mount + poll, because the GM may be adding the missing
  // account in another tab while this sits open (V3/F4).
  const staff = useQuery<StaffRow[]>({
    queryKey: ['staff', 'no-placeholders'], queryFn: () => listStaff(undefined, { excludePlaceholders: true }),
    refetchOnMount: 'always', refetchInterval: 15000,
  });
  const staffList = useMemo(
    () => [...(staff.data ?? [])].sort((a, b) => Number(staffReachable(b)) - Number(staffReachable(a))),
    [staff.data],
  );

  async function submit() {
    if (picked.size === 0) return;
    setBusy(true); setErr(null);
    try {
      const r = await addVoteVoters(roundId, [...picked]);
      const verb = phase === 'nominating' ? '推荐' : '表决';
      toast(`✓ 已加入 ${r.added} 人 —— 本轮票数已相应变化，新成员需完成${verb} / added to the panel`, { kind: 'success', duration: 7000 });
      onAdded();
    } catch (e) { setErr(errText(e, '添加失败 / could not add')); }
    finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ marginTop: 6, padding: 8, width: '100%' }}>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline', marginBottom: 4 }}>
        <strong className="small">添加成员到投票组 / Add panel members</strong>
        <span className="spacer" />
        <button className="small" onClick={onClose}>取消 / cancel</button>
      </div>
      {/* the one consequence worth stating up front: the denominator moves (改改 8/10) */}
      <div className="small muted" style={{ marginBottom: 6 }}>
        新成员需完成本轮{phase === 'nominating' ? '推荐' : '表决'}，票数分母相应增加；已投的票不受影响。
        / Newcomers owe this round's {phase === 'nominating' ? 'recommendation' : 'ballot'} — existing ballots are untouched.
      </div>
      {staff.isLoading && <div className="small muted">载入名册中… / loading roster…</div>}
      <div className="col" style={{ gap: 2, maxHeight: 220, overflowY: 'auto' }}>
        {staffList.map((s) => {
          const already = onPanel.has(s.id);
          return (
            <label key={s.id} className="row small" style={{ gap: 6, opacity: already ? 0.55 : 1 }}>
              <input type="checkbox" disabled={already} checked={already || picked.has(s.id)}
                onChange={() => { const n = new Set(picked); n.has(s.id) ? n.delete(s.id) : n.add(s.id); setPicked(n); }} />
              <span>{s.name}</span>
              <span className="muted">{ROLE_LABEL[s.role] ?? s.role}</span>
              {already && <span className="muted">· 已在组内 / on the panel</span>}
              {!staffReachable(s) && <span title={UNREACHABLE_HINT}> ⚠</span>}
            </label>
          );
        })}
      </div>
      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{err}</div>}
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <button className="primary small" disabled={busy || picked.size === 0} onClick={submit}>
          {busy ? '添加中… / adding…' : `确认添加 ${picked.size} 人 / add`}
        </button>
      </div>
    </div>
  );
}

/**
 * The ballot form — 同意/反对 on MY PENDING SLATE, one submit. A 反对 without a reason is refused
 * client-side AND server-side. Shared by the compare tab and 我的任务 (the PM's surface).
 *
 * The slate is `my_pending_candidate_ids`, not the whole shortlist (改改 fb_828ed0af): a 不可参与
 * company is nobody's ballot, and after a 补充候选 everyone owes a verdict on the ADDITIONS only —
 * asking a voter to re-cast four verdicts to add one is how the round drags. The server accepts
 * exactly this set, so submitting anything else is a 400 by design.
 */
export function VoteBallotForm({ view, onVoted }: { view: VoteRoundView; onVoted?: () => void }) {
  const qc = useQueryClient();
  const [verdicts, setVerdicts] = useState<Record<string, 'approve' | 'object'>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const rid = view.round?.id;
  // still in play; a pre-8/17 payload carries no availability at all, so this degrades to "everything"
  const inPlay = view.candidates.filter((c) => c.availability !== 'unavailable');
  const pendingIds = view.my_pending_candidate_ids;
  const slate = pendingIds ? inPlay.filter((c) => pendingIds.includes(c.id)) : inPlay;
  const isTopUp = slate.length > 0 && slate.length < inPlay.length;
  const missing = slate.filter((c) => !verdicts[c.id]);
  const noteMissing = slate.filter((c) => verdicts[c.id] === 'object' && !(notes[c.id] ?? '').trim());

  /** What still blocks this ballot, one gap per line — spoken on click, not implied by a grey button. */
  function ballotGaps(): string[] {
    const g: string[] = [];
    if (slate.length === 0) g.push('本轮当前没有需要您表决的候选 / nothing on this round is pending for you');
    if (missing.length > 0) {
      g.push(`还有 ${missing.length} 家未表态：${missing.map((c) => c.labor_co_name).join('、')} —— 每家都要选「同意」或「反对」 / ${missing.length} candidate(s) still need an approve/object verdict`);
    }
    if (noteMissing.length > 0) {
      g.push(`反对必须写明理由：${noteMissing.map((c) => c.labor_co_name).join('、')} / an objection needs a written reason`);
    }
    return g;
  }

  async function submit() {
    if (!rid) { toast('本轮投票已失效，请刷新页面 / this vote round is no longer available — refresh the page', { kind: 'error' }); return; }
    const gaps = ballotGaps();
    if (gaps.length > 0) { toastGaps(gaps); return; }
    setBusy(true); setErr(null);
    try {
      const ballots: BallotInput[] = slate.map((c) => ({
        candidate_id: c.id, verdict: verdicts[c.id]!, ...(verdicts[c.id] === 'object' ? { note: notes[c.id].trim() } : {}),
      }));
      const r = await submitVoteBallots(rid, ballots);
      // V7: say it landed. The count is the whole point — "还差谁" is the question everyone asks next.
      const votedNow = r.voters.filter((v) => v.voted_at).length;
      toast(r.completed
        ? `已提交（${votedNow}/${r.voters.length} 票）——本轮投票已完成，可以邀请劳务了 / all votes are in`
        : `已提交（${votedNow}/${r.voters.length} 票）/ ballot recorded`, { kind: 'success' });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['vote-round'] }),
        qc.invalidateQueries({ queryKey: ['my-vote-rounds'] }),
      ]);
      onVoted?.();
    } catch (e) { setErr(errText(e, '投票失败 / vote failed')); }
    finally { setBusy(false); }
  }

  // Nothing in play ⇒ there is no ballot to cast: reconcileVoters deliberately refuses to complete
  // anyone on an empty slate, so the round sits open until its creator tops up or cancels. Showing the
  // usual 「请完成您的一票」 shell with a live submit here only earns a 409 nothing_pending — say whose
  // move it actually is instead. (Shared with 我的任务, which renders this same form.)
  if (slate.length === 0) {
    return (
      <div className="card vote-form" style={{ marginTop: 10 }}>
        <div className="small muted">
          本轮候选均已标记「不可参与」，请等待发起人补充候选或取消本轮。
          / Every candidate is marked unavailable — waiting on the round's owner to top up the slate or cancel.
        </div>
      </div>
    );
  }

  return (
    <div className="card vote-form" style={{ marginTop: 10, borderColor: 'var(--accent)', background: 'var(--accent-soft)' }}>
      <strong className="small">请完成您的一票 / Cast your vote</strong>
      {isTopUp && (
        <div className="small" style={{ marginTop: 2, color: 'var(--accent)' }}>
          补充表决：仅需对新增/待补投的 {slate.length} 家表态，之前投过的票保留。
          / Top-up ballot — only the {slate.length} new candidate(s) need your verdict; your earlier votes stand.
        </div>
      )}
      <div className="small muted" style={{ marginBottom: 6 }}>每家都要表态；反对必须写明理由（反对不会自动淘汰，由发起人/总经理定夺）。/ Vote on every candidate; an objection needs a reason — it flags, it does not eliminate.</div>
      {slate.map((c) => (
        <div key={c.id} style={{ padding: '6px 0', borderTop: '1px solid var(--rule)' }}>
          <div className="row-wrap vote-row vote-verdict-row">
            <strong className="vote-cand-name">{c.labor_co_name}</strong>
            <button className={verdicts[c.id] === 'approve' ? 'btn-primary small vote-verdict' : 'small vote-verdict'}
              onClick={() => setVerdicts({ ...verdicts, [c.id]: 'approve' })}>✓ 同意 / Approve</button>
            <button className={verdicts[c.id] === 'object' ? 'btn-primary small vote-verdict' : 'small vote-verdict'}
              onClick={() => setVerdicts({ ...verdicts, [c.id]: 'object' })}>✗ 反对 / Object</button>
            <span className="spacer" />
            <HistoryChips c={c} />
          </div>
          {verdicts[c.id] === 'object' && (
            <input style={{ width: '100%', marginTop: 4 }} placeholder="反对理由（必填）/ reason for objecting (required)"
              value={notes[c.id] ?? ''} onChange={(e) => setNotes({ ...notes, [c.id]: e.target.value })} />
          )}
        </div>
      ))}
      <div className="row-wrap vote-actions">
        {missing.length > 0 && <span className="small muted">还有 {missing.length} 家未表态 / {missing.length} candidate(s) left</span>}
        {missing.length === 0 && noteMissing.length > 0 && <span className="small" style={{ color: 'var(--bad)' }}>反对必须填写理由 / an objection needs a reason</span>}
        {err && <span className="small" style={{ color: 'var(--bad)' }}>{err}</span>}
        <span className="spacer" />
        <button className="btn-primary" disabled={busy} onClick={submit}>
          {busy ? '提交中… / submitting…' : '提交我的一票 / Submit ballot'}
        </button>
      </div>
    </div>
  );
}

/**
 * 推荐 phase — MY recommendations (改改 fb_85f73594). The panel member browses the WHOLE supplier
 * registry (price-free, each row carrying its 验收/终验 track record), ticks whoever they'd put forward
 * and may say why. Submitting nothing is a real answer — 无补充 — and still counts as done, so the
 * round can advance. 黑名单/停用 rows stay VISIBLE but greyed: seeing why a name is missing beats
 * wondering where it went.
 */
export function VoteNominationForm({ view, onSubmitted }: { view: VoteRoundView; onSubmitted?: () => void }) {
  const qc = useQueryClient();
  const rid = view.round?.id;
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reg = useQuery({ queryKey: ['vote-registry', rid], queryFn: () => voteRoundRegistry(rid!), enabled: !!rid, refetchInterval: 15000 });
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (reg.data?.labor_cos ?? []).filter((c) => !s || [c.name, c.internal_code, c.region, c.trades_json].some((v) => (v ?? '').toLowerCase().includes(s)));
  }, [reg.data, q]);
  const nominated = view.voters.filter((v) => v.nominated_at).length;

  function toggle(c: VoteRegistryCo) {
    if (c.disabled) { toast(c.disabled_reason ?? '该供应商不可选 / this supplier cannot be nominated', { kind: 'error' }); return; }
    const n = new Set(picked); n.has(c.id) ? n.delete(c.id) : n.add(c.id); setPicked(n);
  }

  async function submit() {
    if (!rid) { toast('本轮投票已失效，请刷新页面 / this vote round is no longer available — refresh the page', { kind: 'error' }); return; }
    if (picked.size === 0 && !confirm('确认无补充推荐？\n提交后本轮将按其他同事的推荐汇总名单。\n/ Submit with no recommendations? The slate will be the union of everyone else\'s.')) return;
    setBusy(true); setErr(null);
    try {
      const nominations: NominationInput[] = [...picked].map((cid) => ({ labor_co_id: cid, ...(notes[cid]?.trim() ? { note: notes[cid].trim() } : {}) }));
      const r = await submitVoteNominations(rid, nominations);
      toast(r.advanced
        ? `已提交推荐（${r.voters.length}/${r.voters.length}）——名单已汇总为 ${r.candidates.length} 家，请开始表决 / the slate is in, voting starts now`
        : `已提交推荐（${r.voters.filter((v) => v.nominated_at).length}/${r.voters.length}）${picked.size === 0 ? ' —— 无补充 / no additions' : ''}`,
        { kind: 'success' });
      setPicked(new Set()); setNotes({});
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['vote-round'] }),
        qc.invalidateQueries({ queryKey: ['my-vote-rounds'] }),
        qc.invalidateQueries({ queryKey: ['vote-registry', rid] }),
      ]);
      onSubmitted?.();
    } catch (e) { setErr(errText(e, '提交失败 / could not submit')); }
    finally { setBusy(false); }
  }

  return (
    <div className="card vote-form" style={{ marginTop: 10, borderColor: 'var(--accent)', background: 'var(--accent-soft)' }}>
      <div className="row-wrap vote-row" style={{ gap: 8, alignItems: 'baseline' }}>
        <strong className="small">请推荐劳务队伍 / Recommend labor companies</strong>
        <span className="pill pill-stage small">已推荐 {nominated}/{view.voters.length}</span>
        <span className="spacer" />
        <VoterChips voters={view.voters} phase="nominating" />
      </div>
      <div className="small muted" style={{ margin: '4px 0 6px' }}>
        勾选您愿意推荐的队伍（可写理由）；没有想推荐的就直接提交「无补充」。全部同事提交后，名单自动汇总进入表决。
        / Tick whoever you'd put forward (a reason is optional); submitting nothing is a valid 无补充. The slate is the union of everyone's picks.
      </div>
      <input placeholder="搜索：名称 / 编号 / 工种 / 地区 · search" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: '100%' }} />
      <div className="card vote-picker" style={{ marginTop: 6 }}>
        {reg.isLoading && <div className="muted small" style={{ padding: 10 }}>加载供应商库中… / loading the registry…</div>}
        {reg.isError && (
          <div className="small" style={{ padding: 10, color: 'var(--bad)' }}>
            供应商库加载失败 / could not load the registry — {errText(reg.error)}{' '}
            <button className="small" onClick={() => reg.refetch()}>重试 / Retry</button>
          </div>
        )}
        {!reg.isLoading && !reg.isError && list.length === 0 && <div className="muted small" style={{ padding: 10 }}>无匹配 / no matches</div>}
        {list.map((c) => (
          <div key={c.id} className={`vote-picker-row${c.disabled ? ' is-disabled' : ''}`}>
            <label className="row-wrap vote-picker-label" style={{ cursor: c.disabled ? 'not-allowed' : 'pointer' }}
              title={c.disabled ? (c.disabled_reason ?? '') : undefined}>
              <input type="checkbox" checked={picked.has(c.id)} disabled={c.disabled} onChange={() => toggle(c)} />
              {c.internal_code && <span className="pill pill-muted small">{c.internal_code}</span>}
              <strong>{c.name}</strong>
              {c.disabled && <span className="pill pill-bad small">{c.status === 'suspended' ? '已停用 / suspended' : '黑名单 / blacklisted'}</span>}
              {!c.disabled && c.status && c.status !== 'active' && <span className="pill pill-warn small">{c.status}</span>}
              <span className="spacer" />
              <HistoryChips c={c} />
            </label>
            {picked.has(c.id) && (
              <input style={{ width: '100%', marginTop: 4 }} placeholder="推荐理由（选填）/ why you recommend them (optional)"
                value={notes[c.id] ?? ''} onChange={(e) => setNotes({ ...notes, [c.id]: e.target.value })} />
            )}
          </div>
        ))}
      </div>
      <div className="row-wrap vote-actions">
        <span className="small muted">已选 {picked.size} 家 / {picked.size} picked</span>
        {err && <span className="small" style={{ color: 'var(--bad)' }}>{err}</span>}
        <span className="spacer" />
        <button className="btn-primary" disabled={busy} onClick={submit}>
          {busy ? '提交中… / submitting…' : (picked.size === 0 ? '提交「无补充」/ Submit — nothing to add' : `提交我的推荐（${picked.size}）/ Submit`)}
        </button>
      </div>
    </div>
  );
}

/** A supplier the server will refuse as a candidate (V9) — greyed here so nobody discovers it on submit. */
function coBlockedReason(c: RegistryLaborCo): string | null {
  if (c.status === 'blacklisted') return '已列入黑名单，不能作为候选 / blacklisted — cannot be a candidate';
  if (c.status === 'suspended') return '已停用，不能作为候选 / suspended — cannot be a candidate';
  return null;
}

/**
 * 补充候选 — add companies to an OPEN 表决 round without sending the panel back to 推荐 (改改
 * fb_828ed0af: "只有一家有时间，只好让大家重新推荐一遍"). Everyone then owes a ballot on exactly the
 * additions. The picker is the same registry list the 我来定名单 form uses, minus whoever is already on
 * the slate, with the same 黑名单/停用 greying — a blocked pick must never be discovered on submit.
 */
function AddCandidatesForm({ view, onAdded, onClose }: { view: VoteRoundView; onAdded: () => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [reached, setReached] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rid = view.round?.id;

  const registry = useQuery<RegistryLaborCo[]>({
    // refetchOnMount:'always' is enough — this picker is a short-lived modal, and polling a SHARED
    // query key would keep refetching for every other mounted consumer of it too.
    queryKey: ['registry-labor-cos'], queryFn: listRegistryLaborCos, refetchOnMount: 'always',
  });
  const onSlate = useMemo(() => new Set(view.candidates.map((c) => c.labor_co_id).filter((x): x is string => !!x)), [view.candidates]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (registry.data ?? []).filter((c) => !onSlate.has(c.id))
      .filter((c) => !s || [c.name, c.internal_code, c.region, c.trades_json].some((v) => (v ?? '').toLowerCase().includes(s)));
  }, [registry.data, q, onSlate]);

  async function submit() {
    if (!rid) { toast('本轮投票已失效，请刷新页面 / this vote round is no longer available — refresh the page', { kind: 'error' }); return; }
    if (picked.size === 0) { toastGaps(['请至少勾选一家要补充的劳务 / pick at least one company to add']); return; }
    if (!confirm(`补充 ${picked.size} 家候选？\n补充后所有成员需对新增候选补投一票（之前的票保留）。\n/ Add ${picked.size} candidate(s)? Everyone will owe a ballot on the additions — their earlier votes stand.`)) return;
    setBusy(true); setErr(null);
    try {
      const r = await addVoteCandidates(rid, { labor_co_ids: [...picked], ...(reached ? { availability: 'available' as const } : {}) });
      toast(`已补充 ${r.added} 家候选，全体成员需补投一票 / added ${r.added} — everyone owes a top-up ballot`, { kind: 'success' });
      setPicked(new Set()); onAdded(); onClose();
    } catch (e) { setErr(errText(e, '补充失败 / could not add candidates')); }
    finally { setBusy(false); }
  }

  return (
    <div className="card vote-form" style={{ marginTop: 10, borderColor: 'var(--accent)', background: 'var(--accent-soft)' }}>
      <strong className="small">补充候选 / Add candidates</strong>
      <div className="small muted" style={{ margin: '4px 0 6px' }}>
        联系下来能参与的家数不够时，从供应商库直接补充——不必让投票组重新推荐一遍。
        / When too few of the slate can take part, top it up from the registry instead of re-running 推荐.
      </div>
      <input placeholder="搜索：名称 / 编号 / 工种 / 地区 · search" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: '100%' }} />
      <div className="card vote-picker vote-picker-sm" style={{ marginTop: 6 }}>
        {registry.isLoading && <div className="muted small" style={{ padding: 10 }}>加载供应商库中… / loading…</div>}
        {registry.isError && (
          <div className="small" style={{ padding: 10, color: 'var(--bad)' }}>
            供应商库加载失败 / could not load the registry — {errText(registry.error)}{' '}
            <button className="small" onClick={() => registry.refetch()}>重试 / Retry</button>
          </div>
        )}
        {!registry.isLoading && !registry.isError && list.length === 0 && (
          <div className="muted small" style={{ padding: 10 }}>无可补充的劳务（其余均已在名单内）/ nothing left to add — the rest are already on the slate</div>
        )}
        {list.map((c) => {
          const blocked = coBlockedReason(c);
          return (
            <label key={c.id} className={`row-wrap vote-picker-row vote-picker-label${blocked ? ' is-disabled' : ''}`}
              title={blocked ?? undefined} style={{ cursor: blocked ? 'not-allowed' : 'pointer' }}>
              <input type="checkbox" checked={picked.has(c.id)} disabled={!!blocked}
                onChange={() => { const n = new Set(picked); n.has(c.id) ? n.delete(c.id) : n.add(c.id); setPicked(n); }} />
              {c.internal_code && <span className="pill pill-muted small">{c.internal_code}</span>}
              <strong>{c.name}</strong>
              {blocked ? <span className="pill pill-bad small">{c.status === 'suspended' ? '已停用 / suspended' : '黑名单 / blacklisted'}</span>
                : c.status !== 'active' && <span className="pill pill-warn small">{c.status}</span>}
            </label>
          );
        })}
      </div>
      <label className="row-wrap small" style={{ gap: 6, alignItems: 'center', marginTop: 6, cursor: 'pointer' }}>
        <input type="checkbox" checked={reached} onChange={(e) => setReached(e.target.checked)} />
        已联系可参与（否则记为「未联系」）/ already phoned and they can take part (otherwise recorded as 未联系)
      </label>
      <div className="row-wrap vote-actions">
        <span className="small muted">已选 {picked.size} 家 / {picked.size} picked</span>
        {err && <span className="small" style={{ color: 'var(--bad)' }}>{err}</span>}
        <span className="spacer" />
        <button className="small" onClick={onClose}>取消 / Cancel</button>
        <button className="btn-primary" disabled={busy} onClick={submit}>
          {busy ? '补充中… / adding…' : `补充所选（${picked.size}）/ Add`}
        </button>
      </div>
    </div>
  );
}

/**
 * 估价/采购/GM open a round. Two ways in (改改 fb_85f73594):
 *   · 由投票组推荐 — name only the panel; they propose the slate (the default, and the meeting's ask);
 *   · 我来定名单   — name the shortlist yourself; the panel votes on it (the original flow).
 * Either way the panel must carry ≥1 商务 and ≥1 工程, and picking someone who has never logged in and
 * is not 企业微信-bound is called out BEFORE and AFTER creation — that voter may never come (finding b).
 */
function StartVoteForm({ pid, onStarted, label }: { pid: string; onStarted: () => void; label?: string }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'nominating' | 'voting'>('nominating');
  const [q, setQ] = useState('');
  const [cands, setCands] = useState<Set<string>>(new Set());
  const [voters, setVoters] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // V3/F4: the roster and the registry change WHILE this form sits open (the GM adds the missing 商务
  // in another tab). Always refetch on mount and keep polling, so the picker is never stale.
  const registry = useQuery<RegistryLaborCo[]>({
    queryKey: ['registry-labor-cos'], queryFn: listRegistryLaborCos,
    enabled: open, refetchOnMount: 'always', refetchInterval: open ? 15000 : false,
  });
  const staff = useQuery<StaffRow[]>({
    queryKey: ['staff', 'no-placeholders'], queryFn: () => listStaff(undefined, { excludePlaceholders: true }),
    enabled: open, refetchOnMount: 'always', refetchInterval: open ? 15000 : false,
  });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (registry.data ?? []).filter((c) => !s || [c.name, c.internal_code, c.region, c.trades_json].some((v) => (v ?? '').toLowerCase().includes(s)));
  }, [registry.data, q]);

  // /staff already returns ACTIVE, non-placeholder rows only — so this list is exactly what's selectable.
  // Unreachable people sort to the BOTTOM: they are pickable (sometimes you must), never the easy default.
  const staffList = useMemo(
    () => [...(staff.data ?? [])].sort((a, b) => Number(staffReachable(b)) - Number(staffReachable(a))),
    [staff.data],
  );
  const picked = staffList.filter((s) => voters.has(s.id));
  const REQUIRED = [
    { role: 'commercial', label: '商务 / commercial', zh: '商务' },
    { role: 'site_pm', label: '工程（项目经理）/ site_pm', zh: '工程（项目经理）' },
  ];
  const missing = REQUIRED.filter((r) => !picked.some((s) => s.role === r.role));
  const missingRoles = missing.map((r) => r.label);
  /** Roles with NO real account in the system at all — the prod case behind 「点击没有反应」 (fb_08cfc4e3). */
  const absentFromRoster = staff.data ? REQUIRED.filter((r) => !staffList.some((s) => s.role === r.role)) : [];
  const toggle = (set: Set<string>, put: (s: Set<string>) => void, id: string) => {
    const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); put(n);
  };

  /** What still blocks the round — and, when a role simply doesn't exist yet, where to create it. */
  function startGaps(): string[] {
    const g: string[] = [];
    if (mode === 'voting' && cands.size < 1) g.push('请先勾选至少一家候选劳务，或改用「由投票组推荐」/ pick at least one candidate, or switch to panel-nominated');
    if (missing.length > 0) {
      const unavailable = missing.filter((r) => absentFromRoster.some((a) => a.role === r.role));
      g.push(unavailable.length > 0
        ? `投票组还缺：${missingRoles.join('、')} —— 系统里还没有「${unavailable.map((r) => r.zh).join('、')}」角色的真实账号，请总经理在 管理→添加成员 添加（仅总经理可操作）/ the panel needs the role(s) above — no real account with that role exists yet; ask the GM to add one under Admin → Add member`
        : `投票组还缺：${missingRoles.join('、')} —— 商务与工程各至少一人；若系统里还没有该角色的真实账号，请在 管理→添加成员 先添加 / the panel needs at least one of each — if no real account with that role exists yet, add one under Admin → Add member first`);
    }
    if (missing.length === 0 && voters.size < 2) g.push('投票组至少两人 / the panel needs at least two voters');
    return g;
  }

  async function start() {
    const gaps = startGaps();
    if (gaps.length > 0) { toastGaps(gaps); return; }
    setBusy(true); setErr(null);
    try {
      const created = await createVoteRound(pid, {
        ...(mode === 'voting' ? { candidate_labor_co_ids: [...cands] } : {}),
        voter_staff_ids: [...voters], note: note.trim() || undefined,
      });
      // (b) Say it out loud at the moment it matters: these people may never see the round, and an open
      // round blocks 邀请劳务 until they act. Naming them now is cheaper than a week of silence.
      const unreachable = picked.filter((s) => !staffReachable(s));
      // 改改 8/5: mapped members get a workbench task automatically — the UNmapped ones are exactly the
      // people who still need the individual heads-up, so name them here too.
      const taskUnmapped = (created.voters ?? []).filter((v) => v.task_reachable === false).map((v) => v.staff_name);
      // L2: even a MAPPED member's mint can fail outright (e.g. pm:task perms not yet granted) — that
      // is invisible unless we read vote_tasks and say so here, not just the mapping gap above.
      const failedMints = (created.vote_tasks ?? []).filter((t) => t.status === 'failed');
      const warn = unreachable.length > 0 || taskUnmapped.length > 0 || failedMints.length > 0;
      toast(warn
        ? <span>
            <div>✓ 已发起{mode === 'nominating' ? '推荐' : '投票'} / round started</div>
            {unreachable.length > 0 && (
              <div>⚠ {unreachable.map((s) => s.name).join('、')} 未绑定企业微信且从未登录，可能收不到通知、无法投票——请提醒本人登录，或在投票组中移除。
                / may never receive the notification — remind them to sign in, or remove them from the panel.</div>
            )}
            {taskUnmapped.length > 0 && (
              <div>⚠ {taskUnmapped.join('、')} 未映射到任务平台，工作台不会自动出现投票任务——请单独通知，或请总经理在任务面板执行「同步指派人」。
                / not mapped to the task platform, so no workbench task will appear — notify them individually, or ask the GM to run assignee sync.</div>
            )}
            {failedMints.length > 0 && (
              <div>⚠ {failedMints.length} 条任务未能发送到任务平台（可能是权限未开通），成员的工作台不会出现任务——请单独通知。
                / {failedMints.length} task(s) failed to reach the task platform — workbench tasks will not appear; notify members directly.</div>
            )}
          </span>
        : `已发起${mode === 'nominating' ? '推荐' : '投票'} / round started`,
        { kind: warn ? 'error' : 'success', duration: warn ? 9000 : 4000 });
      setCands(new Set()); setVoters(new Set()); setNote(''); setOpen(false);
      onStarted();
    } catch (e) { setErr(errText(e, '发起失败 / could not start the round')); }
    finally { setBusy(false); }
  }

  if (!open) return <button className="btn-primary" style={{ marginTop: 8 }} onClick={() => setOpen(true)}>{label ?? '🗳 发起定标投票 / Start a vote'}</button>;

  return (
    <div style={{ marginTop: 10 }}>
      <div className="row-wrap small" style={{ gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <label className="vote-mode" style={{ cursor: 'pointer' }}>
          <input type="radio" checked={mode === 'nominating'} onChange={() => setMode('nominating')} />{' '}
          <strong>由投票组推荐名单</strong> / panel nominates
        </label>
        <label className="vote-mode" style={{ cursor: 'pointer' }}>
          <input type="radio" checked={mode === 'voting'} onChange={() => setMode('voting')} />{' '}
          <strong>我来定名单</strong> / I set the shortlist
        </label>
        <span className="muted vote-mode-hint">
          {mode === 'nominating'
            ? '投票组每人先在供应商库里推荐（可不推荐），全部提交后自动汇总名单再表决。/ each member recommends first; the union becomes the slate.'
            : '直接勾选候选劳务，投票组只做表决。/ you pick the candidates; the panel only votes.'}
        </span>
      </div>
      <div className="row-wrap vote-cols">
        {mode === 'voting' && (
        <div className="vote-col vote-col-wide">
          <div className="field" style={{ marginBottom: 4 }}><label>候选劳务 / Candidates ({cands.size})</label>
            <input placeholder="搜索：名称 / 编号 / 工种 / 地区 · search" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div className="card vote-picker vote-picker-sm">
            {registry.isLoading && <div className="muted small" style={{ padding: 10 }}>加载供应商库中… / loading…</div>}
            {registry.isError && (
              <div className="small" style={{ padding: 10, color: 'var(--bad)' }}>
                供应商库加载失败 / could not load the registry — {errText(registry.error)}{' '}
                <button className="small" onClick={() => registry.refetch()}>重试 / Retry</button>
              </div>
            )}
            {!registry.isLoading && !registry.isError && filtered.length === 0 && <div className="muted small" style={{ padding: 10 }}>无匹配 / no matches</div>}
            {filtered.map((c) => {
              const blocked = coBlockedReason(c);
              return (
                <label key={c.id} className={`row-wrap vote-picker-row vote-picker-label${blocked ? ' is-disabled' : ''}`}
                  title={blocked ?? undefined} style={{ cursor: blocked ? 'not-allowed' : 'pointer' }}>
                  <input type="checkbox" checked={cands.has(c.id)} disabled={!!blocked} onChange={() => toggle(cands, setCands, c.id)} />
                  {c.internal_code && <span className="pill pill-muted small">{c.internal_code}</span>}
                  <strong>{c.name}</strong>
                  {blocked ? <span className="pill pill-bad small">{c.status === 'suspended' ? '已停用 / suspended' : '黑名单 / blacklisted'}</span>
                    : c.status !== 'active' && <span className="pill pill-warn small">{c.status}</span>}
                </label>
              );
            })}
          </div>
        </div>
        )}
        <div className="vote-col">
          <div className="field" style={{ marginBottom: 4 }}><label>投票组 / Voter panel ({voters.size})</label></div>
          {/* on a phone .vote-picker drops its own scroll well, so the absentFromRoster banner below
              stays on screen instead of hiding inside a 220px box (the fb_08cfc4e3 failure mode) */}
          <div className="card vote-picker vote-picker-sm">
            {staff.isLoading && <div className="muted small" style={{ padding: 10 }}>加载中… / loading…</div>}
            {/* The panel rule can't be met if the role has no real account yet — say so here, not only on click. */}
            {absentFromRoster.map((r) => (
              <div key={r.role} className="small" style={{ padding: '6px 10px', background: 'var(--warn-soft)', color: 'var(--accent)', borderBottom: '1px solid var(--rule)' }}>
                ⚠ 系统里还没有「{r.zh}」角色的真实账号，无法满足投票组要求 — 请总经理在 管理→添加成员 添加后再发起 / no real {r.role === 'commercial' ? '商务' : '工程'} account exists yet — ask the GM to add one under Admin → Add member
              </div>
            ))}
            {staffList.map((s) => (
              <label key={s.id} className="row-wrap vote-picker-row vote-picker-label" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={voters.has(s.id)} onChange={() => toggle(voters, setVoters, s.id)} />
                <strong>{s.name}</strong>
                <span className={`pill small ${s.role === 'commercial' || s.role === 'site_pm' ? 'pill-stage' : 'pill-muted'}`}>{ROLE_LABEL[s.role] ?? s.role}</span>
                {/* (b) still pickable — but never silently: an unreachable voter is how a round hangs. */}
                {!staffReachable(s) && <span className="pill pill-bad small" title={UNREACHABLE_HINT}>⚠ 未绑定登录，可能收不到通知/无法投票</span>}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
        <label>说明 / Note (optional)</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="例如：本次三家均做过同类精装 / e.g. all three have done comparable fit-out" />
      </div>
      <div className="row-wrap vote-actions">
        {mode === 'voting' && cands.size < 1 && <span className="small" style={{ color: 'var(--bad)' }}>请至少勾选一家候选劳务 / pick at least one candidate</span>}
        {missingRoles.length > 0 && <span className="small" style={{ color: 'var(--bad)' }}>投票组还缺：{missingRoles.join('、')} — 商务与工程各至少一人 / the panel needs at least one of each</span>}
        {missingRoles.length === 0 && voters.size < 2 && <span className="small muted">投票组至少两人 / at least two voters</span>}
        {err && <span className="small" style={{ color: 'var(--bad)' }}>{err}</span>}
        <span className="spacer" />
        <button className="small" onClick={() => setOpen(false)}>取消 / Cancel</button>
        <button className="btn-primary" disabled={busy} onClick={start}>
          {busy ? '发起中… / starting…' : (mode === 'nominating' ? '发起推荐 / Start nominating' : '发起投票 / Start vote')}
        </button>
      </div>
    </div>
  );
}

/**
 * 历轮汇总 — the package's slate ACROSS rounds (杨丽刚 fb_712b4d4b). Once round 1 closed, the only way to
 * add names was round 2, and the panel then showed round 2 alone: round 1's winner "disappeared" and
 * the 不少于三家 line counted 2 while the package actually had 3. One row per company (later round
 * wins), tagged 第几轮; 联系结果 stays recordable here — it lands on that company's own round row.
 */
function PooledSlate({ pool, canMark = false, showShort = false, onChanged }: {
  pool: VotePool; canMark?: boolean; showShort?: boolean; onChanged?: () => void;
}) {
  const order: Record<VoteAvailability, number> = { available: 0, unknown: 1, unavailable: 2 };
  const rows = [...pool.candidates].sort((a, b) => order[a.availability] - order[b.availability] || b.round_seq - a.round_seq);
  return (
    <div style={{ marginTop: 10, padding: '8px 10px', border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', background: 'var(--paper)' }}>
      <div className="row-wrap small" style={{ gap: 8, alignItems: 'baseline' }}>
        <strong>历轮汇总 / Across {pool.rounds} rounds</strong>
        <span className="muted">本包所有投票轮次的候选，一家一行；仍在名单内 {pool.available_count} 家。/ every candidate this package has voted on; {pool.available_count} still in play.</span>
      </div>
      {showShort && pool.short_by > 0 && (
        <div className="small" style={{ marginTop: 4, color: 'var(--bad)' }}>
          ⚠ 历轮合计仍在名单内的候选不足 {pool.min_bidders} 家（当前 {pool.available_count} 家）——公开招标需不少于 {pool.min_bidders} 家。
          / Only {pool.available_count} candidate(s) across all rounds are still in play — an open tender needs at least {pool.min_bidders}.
        </div>
      )}
      {rows.map((c) => <PooledRow key={c.id} c={c} canMark={canMark} onChanged={onChanged} />)}
    </div>
  );
}

function PooledRow({ c, canMark, onChanged }: { c: VotePoolCandidate; canMark: boolean; onChanged?: () => void }) {
  const [busy, setBusy] = useState<VoteAvailability | null>(null);
  const av: VoteAvailability = c.availability ?? 'unknown';
  const out = av === 'unavailable';
  async function mark(next: VoteAvailability) {
    if (next === busy) return;
    let note: string | undefined;
    if (next === 'unavailable') {
      const r = prompt(`「${c.labor_co_name}」不可参与的原因？（可不填）/ Why can't they take part? (optional)`);
      if (r === null) return;
      note = r.trim() || undefined;
    }
    setBusy(next);
    try {
      await setVoteCandidateAvailability(c.round_id, c.id, { availability: next, ...(note ? { note } : {}) });
      toast(`已记录「${c.labor_co_name}」：${VOTE_AVAILABILITY_LABEL[next].label}`, { kind: 'success' });
      onChanged?.();
    } catch (e) { toast(errText(e, '记录联系结果失败 / could not record the contact result'), { kind: 'error', duration: 7000 }); }
    finally { setBusy(null); }
  }
  return (
    <div className="row-wrap vote-row small" style={{ gap: 8, alignItems: 'baseline', padding: '5px 0', borderTop: '1px solid var(--rule)', opacity: out ? 0.55 : 1 }}>
      <strong>{c.labor_co_name}</strong>
      <span className="pill small pill-muted" title="来自第几轮投票 / which round named them">第 {c.round_seq} 轮</span>
      <span className={`pill small ${VOTE_AVAILABILITY_LABEL[av].pill}`}>{VOTE_AVAILABILITY_LABEL[av].label}</span>
      <span className={`pill small ${c.approve ? 'pill-good' : 'pill-muted'}`}>同意 {c.approve}</span>
      <span className={`pill small ${c.object ? 'pill-bad' : 'pill-muted'}`}>反对 {c.object}</span>
      {(c.availability_by || c.availability_note) && (
        <span className="muted">{c.availability_by ?? '—'}{c.availability_at ? ` · ${fmtLocal(c.availability_at)}` : ''}{c.availability_note ? ` — ${c.availability_note}` : ''}</span>
      )}
      <span className="spacer" />
      {canMark && AVAILABILITY_ACTIONS.map((a) => (
        <button key={a.value} className={av === a.value ? 'btn-primary small' : 'small'} disabled={!!busy} onClick={() => mark(a.value)}>
          {busy === a.value ? '记录中…' : a.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The decision record of an earlier, finished round — kept one click away (V4).
 *
 * 联系结果 stays recordable here (`canMark`): 预算 phones the winners AFTER the vote closes, and a
 * completed round is exactly where that answer belongs. The server treats it as informational — a
 * completed round is never reopened and no ballot moves.
 */
function CompletedRoundSummary({ v, canMark = false, hideShort = false, onChanged }: { v: VoteRoundView; canMark?: boolean; hideShort?: boolean; onChanged?: () => void }) {
  const [open, setOpen] = useState(false);
  if (!v.round) return null;
  const objections = v.ballots.filter((b) => b.verdict === 'object');
  const short = !hideShort && (v.short_by ?? 0) > 0;   // the pooled banner speaks for the package when there are several rounds
  return (
    <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed var(--rule)' }}>
      <button className="small chip-disclose flush" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? '▾' : '▸'} 上一次已完成的投票记录（{v.candidates.length} 家候选，{objections.length} 条反对）
        {v.round.completed_at ? ` · ${v.round.completed_at.slice(0, 10)}` : ''} / previous completed round
      </button>
      {open && (
        <div style={{ marginTop: 4 }}>
          <div className="row-wrap small vote-row" style={{ gap: 8, alignItems: 'baseline' }}>
            <span className={`pill small ${VOTE_STATUS_LABEL.completed.pill}`}>{VOTE_STATUS_LABEL.completed.label}</span>
            <span className="muted">发起人 {v.round.created_by_name ?? '—'}</span>
            <span className="spacer" />
            <VoterChips voters={v.voters} />
          </div>
          {short && (
            <div className="small" style={{ marginTop: 4, color: 'var(--bad)' }}>
              ⚠ 仍在名单内的候选不足 {v.min_bidders} 家（当前 {v.available_count} 家）——建议「重新发起投票」补充推荐。
              / fewer than {v.min_bidders} candidates are still in play ({v.available_count}) — start another round to top the slate up.
            </div>
          )}
          {v.candidates.map((c) => <CandidateRow key={c.id} v={v} c={c} canMark={canMark} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  );
}

export default function VotePanel({ pid }: { pid: string }) {
  const qc = useQueryClient();
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  // F4/V3: the round moves while this page is open — someone else votes, the phase flips, a voter is
  // dropped. Poll while it is OPEN; a settled round needs no polling.
  const { data: view, isLoading, isError, error, refetch } = useQuery<VoteRoundView>({
    queryKey: ['vote-round', pid], queryFn: () => getLatestVoteRound(pid), enabled: !!pid,
    refetchInterval: (q) => (q.state.data?.round?.status === 'open' ? 15000 : false),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [adding, setAdding] = useState(false);
  // BOTH queries: every lever on this panel can move 我的任务 and the nav badge too — a top-up reopens
  // people, an advance flips the phase, a 联系结果 can complete the round. The ballot path already does
  // this; leaving it out here is how a member's badge sat there claiming work that was no longer theirs.
  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: ['vote-round', pid] }),
    qc.invalidateQueries({ queryKey: ['my-vote-rounds'] }),
  ]);

  const acct = me?.account ?? null;
  const isInternal = acct?.subject_type === 'staff';
  const canManage = isInternal && acct.role !== 'site_pm';   // 估价/采购/GM start + cancel rounds
  // 联系结果 belongs to whoever PHONES the labor company — 预算/采购 and the GM. Deliberately narrower
  // than canManage (which includes 商务): the server guard is exactly this set, so showing the buttons
  // to anyone else is offering a guaranteed 403.
  const canMark = isInternal && (acct.role === 'gm' || acct.role === 'procurement' || acct.role === 'budget');

  // V8: a failed load must never vanish into nothing. Say what broke and offer the retry — the invite
  // button elsewhere stays NORMAL, because "we don't know" is not the same as "a round is open".
  if (isError) {
    return (
      <div className="card" style={{ marginBottom: 12, borderColor: 'var(--bad)' }}>
        <div className="row-wrap vote-row" style={{ gap: 8, alignItems: 'baseline' }}>
          <strong>🗳 定标投票 / Recommendation vote</strong>
          <span className="pill pill-bad small">加载失败 / load failed</span>
          <span className="spacer" />
          <button className="small" onClick={() => refetch()}>重试 / Retry</button>
        </div>
        <div className="small" style={{ marginTop: 4, color: 'var(--bad)' }}>{errText(error, '无法读取投票状态 / could not read the vote state')}</div>
      </div>
    );
  }
  if (isLoading || !view) return null;

  const round = view.round;
  const myVoter = round ? view.voters.find((v) => v.staff_id === acct?.subject_ref) : undefined;
  const isCreatorOrGm = !!round && canManage && (acct.role === 'gm' || round.created_by_staff_id === acct.subject_ref);

  // never voted (or the last round was cancelled) — explain the step, offer to start one
  if (!round || round.status === 'cancelled') {
    return (
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="row-wrap vote-row" style={{ gap: 8, alignItems: 'baseline' }}>
          <strong>🗳 定标投票 / Recommendation vote</strong>
          {round?.status === 'cancelled' && <span className={`pill small ${VOTE_STATUS_LABEL.cancelled.pill}`}>上一轮{VOTE_STATUS_LABEL.cancelled.label}</span>}
          <span className="spacer" />
          <span className="muted small">邀请劳务前的一步 / the step before 邀请劳务</span>
        </div>
        <p className="muted small" style={{ margin: '6px 0 0' }}>
          推荐劳务不再走微信群：在系统里指定投票组（至少一名<strong>商务</strong>、一名<strong>工程</strong>）——
          名单可以由投票组自己推荐，也可以由发起人直接给出；票投齐了才能邀请劳务。
          反对要写理由，公开可见，但不会自动淘汰——由发起人或总经理定夺。
          / Name the panel in-system; the slate can come from the panel itself or from you. Inviting unlocks only when every vote is in. Objections are open and reasoned, never automatic.
        </p>
        {canManage && <StartVoteForm pid={pid} onStarted={refresh} />}
        {view.pool && view.pool.rounds > 1 && <PooledSlate pool={view.pool} canMark={canMark} showShort onChanged={refresh} />}
        {view.last_completed && <CompletedRoundSummary v={view.last_completed} canMark={canMark} hideShort={(view.pool?.rounds ?? 0) > 1} onChanged={refresh} />}
      </div>
    );
  }

  const isOpen = round.status === 'open';
  const isNominating = isOpen && round.phase === 'nominating';
  const objections = view.ballots.filter((b) => b.verdict === 'object');
  const acted = view.voters.filter((v) => (isNominating ? v.nominated_at : v.voted_at)).length;
  // 不少于三家, judged on who is STILL IN PLAY (未联系 counts — they may yet say yes). Advisory only:
  // the invite gate reads STATUS, never this.
  const short = (view.short_by ?? 0) > 0 && (round.phase === 'voting' || round.status === 'completed');
  const pooledRounds = view.pool?.rounds ?? 0;
  // who the 催一下 would chase — mirrors the server's rule so the button is never a guaranteed 409
  // …and `staff_id`, because that is what the server can actually address: a panel seat with no
  // mapped identity is still outstanding, but it is not one of the N the 催一下 will reach.
  const stillOwed = view.voters.filter((v) => (isNominating ? !v.nominated_at : !v.voted_at) && !!v.staff_id);
  const skippable = view.voters.filter((v) => !v.nominated_at);
  const lastNudged = round.last_nudged_at ?? null;

  /** 催一下 — one ping to exactly the people who still owe this phase. The toast reports the count and,
   *  when no channel is configured, admits that the nudge only reached the audit trail. */
  async function nudge() {
    setBusy(true); setErr(null);
    try {
      const r = await nudgeVoteRound(round!.id);
      const dark = !r.channels.wecom && !r.channels.chat;
      toast(dark
        ? <span>
            <div>✓ 已催 {r.nudged} 人 / nudged {r.nudged}</div>
            <div>⚠ 两个通知通道都未配置，仅记录在案——请另行告知本人。/ no channel is configured — recorded only; tell them directly.</div>
          </span>
        : `已催 ${r.nudged} 人 / nudged ${r.nudged}`,
        { kind: dark ? 'error' : 'success', duration: dark ? 9000 : 4000 });
      refresh();
    } catch (e) { toast(errText(e, '催办失败 / could not nudge'), { kind: 'error', duration: 7000 }); }
    finally { setBusy(false); }
  }

  /** 提前进入表决 — stamp the silent members as 无补充 and flip on whatever WAS recommended. */
  async function advance() {
    const names = skippable.map((v) => v.staff_name).join('、');
    if (!confirm(`提前进入表决？\n以下成员将被记为「无补充」：${names}\n跳过的成员本轮不能再推荐，此操作不可撤销。\n/ Advance to voting? The members above are recorded as 无补充 — they can no longer nominate in this round, and this cannot be undone.`)) return;
    setBusy(true); setErr(null);
    try {
      const r = await advanceVoteRound(round!.id);
      toast(`已进入表决${r.skipped.length ? `，跳过 ${r.skipped.length} 人（${r.skipped.join('、')}）` : ''} / advanced to voting`, { kind: 'success' });
      refresh();
    } catch (e) { toast(errText(e, '进入表决失败 / could not advance'), { kind: 'error', duration: 7000 }); }
    finally { setBusy(false); }
  }
  // L2: a mint failure (e.g. pm:task perms not yet granted) is otherwise invisible after the create-time
  // toast is dismissed — keep it visible for the life of the open round, not just at creation.
  const failedTasks = (view.vote_tasks ?? []).filter((t) => t.status === 'failed');

  return (
    <div className="card" style={{ marginBottom: 12, borderColor: isOpen ? 'var(--warn)' : undefined }}>
      <div className="row-wrap vote-row" style={{ gap: 8, alignItems: 'baseline' }}>
        <strong>🗳 定标投票 / Recommendation vote</strong>
        <span className={`pill small ${VOTE_STATUS_LABEL[round.status].pill}`}>{VOTE_STATUS_LABEL[round.status].label}</span>
        {isOpen && <span className={`pill small ${VOTE_PHASE_LABEL[round.phase].pill}`}>{VOTE_PHASE_LABEL[round.phase].label}</span>}
        <span className="small">{isNominating ? `已推荐 ${acted}/${view.voters.length} 人 / recommended` : `已投 ${acted}/${view.voters.length} 票 / votes in`}</span>
        <span className="spacer" />
        <VoterChips voters={view.voters} phase={isNominating ? 'nominating' : 'voting'}
          roundId={round.id} canManage={isOpen && isCreatorOrGm} onChanged={refresh} />
      </div>
      {round.note && <div className="small muted" style={{ marginTop: 4 }}>{round.note}</div>}
      {isOpen && (
        <div className="small" style={{ marginTop: 4, color: 'var(--accent)' }}>
          {isNominating
            ? `投票组正在推荐名单，暂不能邀请劳务（发起人 ${round.created_by_name ?? '—'}）。/ The panel is still nominating — inviting stays blocked.`
            : `票未投齐，暂不能邀请劳务（发起人 ${round.created_by_name ?? '—'}）。/ Inviting is blocked until all votes are in.`}
        </div>
      )}
      {view.nomination_stalled && (
        <div className="small" style={{ marginTop: 4, color: 'var(--bad)' }}>
          ⚠ 本轮无人推荐劳务：投票组已全部提交但名单为空。请取消本轮后由发起人直接给出名单。
          / Nobody nominated anyone — cancel this round and set the shortlist yourself.
        </div>
      )}
      {short && (
        <div className="small" style={{ marginTop: 4, color: 'var(--bad)' }}>
          ⚠ {pooledRounds > 1 ? '历轮合计' : ''}仍在名单内的候选不足 {view.min_bidders} 家（当前 {view.available_count} 家）——公开招标需不少于 {view.min_bidders} 家，建议
          {isOpen ? '「补充候选」或让投票组补充推荐' : '「重新发起投票」补充推荐'}。
          / Only {view.available_count} candidate(s){pooledRounds > 1 ? ' across all rounds' : ''} are still in play — an open tender needs at least {view.min_bidders}. Top the slate up.
        </div>
      )}
      {view.pool && pooledRounds > 1 && <PooledSlate pool={view.pool} canMark={canMark} onChanged={refresh} />}
      {isOpen && failedTasks.length > 0 && (
        <div className="small" style={{ marginTop: 4, color: 'var(--bad)' }}>
          ⚠ {failedTasks.length} 条任务未能发送到任务平台（可能是权限未开通），成员的工作台不会出现任务——请单独通知。
          / {failedTasks.length} task(s) failed to reach the task platform — workbench tasks will not appear; notify members directly.
        </div>
      )}
      {!isOpen && (
        <div className="small muted" style={{ marginTop: 4 }}>
          {objections.length > 0
            ? <span style={{ color: 'var(--bad)' }}>⚠ 有 {objections.length} 条反对意见，请在定标前处理。/ {objections.length} objection(s) to resolve before awarding. </span>
            : null}
          反对不会自动淘汰，由发起人或总经理定夺。/ An objection flags a candidate; it never eliminates one — the creator or the GM decides.
        </div>
      )}

      {/* who recommended whom — the 推荐 phase's own record, kept visible after the flip */}
      {(view.nominations?.length ?? 0) > 0 && (
        <div className="small muted" style={{ marginTop: 6 }}>
          {view.nominations!.map((n) => (
            <div key={n.id}>➕ {n.staff_name ?? '—'} 推荐 {n.labor_co_name ?? n.labor_co_id}{n.note ? <span style={{ color: 'var(--ink)' }}> — {n.note}</span> : null}</div>
          ))}
        </div>
      )}

      {/* 联系结果 is 估价/采购/GM's to record — open or completed alike (a cancelled round returned above) */}
      {view.candidates.map((c) => <CandidateRow key={c.id} v={view} c={c} canMark={canMark} onChanged={refresh} />)}

      {isNominating && myVoter && !myVoter.nominated_at && <VoteNominationForm view={view} onSubmitted={refresh} />}
      {isNominating && myVoter?.nominated_at && <div className="small pill pill-good" style={{ marginTop: 8, display: 'inline-block' }}>✓ 您已提交推荐 / you have recommended</div>}
      {isOpen && !isNominating && myVoter && !myVoter.voted_at && <VoteBallotForm view={view} onVoted={refresh} />}
      {isOpen && !isNominating && myVoter?.voted_at && <div className="small pill pill-good" style={{ marginTop: 8, display: 'inline-block' }}>✓ 您已投票 / you have voted</div>}

      {/* 补充候选 — inline, right under the slate it is topping up */}
      {isOpen && !isNominating && isCreatorOrGm && adding && (
        <AddCandidatesForm view={view} onAdded={refresh} onClose={() => setAdding(false)} />
      )}

      {isOpen && isCreatorOrGm && (
        <div className="row-wrap vote-actions" style={{ marginTop: 10 }}>
          {err && <span className="small" style={{ color: 'var(--bad)' }}>{err}</span>}
          {/* the three levers 预算 reaches for instead of phoning four colleagues (改改 fb_828ed0af) */}
          {stillOwed.length > 0 && (
            <button className="small" disabled={busy} onClick={nudge}
              title={`提醒仍未${isNominating ? '推荐' : '表决'}的 ${stillOwed.length} 人：${stillOwed.map((v) => v.staff_name).join('、')}。每轮 30 分钟内只能催一次（发起人与总经理共用这个间隔）。/ remind whoever still owes this phase — once per 30 min per round, shared by the creator and the GM`}>
              🔔 催一下 / Nudge（{stillOwed.length}）
            </button>
          )}
          {lastNudged && (
            <span className="small muted" title={fmtLocal(lastNudged)}>
              上次催办 {fmtLocal(lastNudged, 'time')} / last nudged
            </span>
          )}
          {isNominating && (view.nominations?.length ?? 0) > 0 && skippable.length > 0 && (
            <button className="small" disabled={busy} onClick={advance}
              title={`把仍未推荐的 ${skippable.length} 人记为「无补充」并直接进入表决；跳过的成员本轮不能再推荐 / stamp the ${skippable.length} silent member(s) as 无补充 and flip — they can no longer nominate in this round`}>
              ⏩ 提前进入表决 / Advance to voting
            </button>
          )}
          {!isNominating && !adding && (
            <button className="small" disabled={busy} onClick={() => setAdding(true)}
              title="从供应商库补充候选；补充后所有成员需对新增候选补投一票 / add candidates from the registry — everyone will owe a ballot on the additions">
              ➕ 补充候选 / Add candidates
            </button>
          )}
          <span className="spacer" />
          {cancelling && (
            <input className="small vote-cancel-reason" autoFocus value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)} placeholder="取消原因（可不填）/ reason (optional)" />
          )}
          <button className="small" disabled={busy} style={{ color: 'var(--bad)' }} onClick={async () => {
            if (!cancelling) { setCancelling(true); return; }
            if (!confirm('取消本轮定标投票？取消后可重新发起，且不再阻止邀请劳务；未表态的成员会收到通知。/ Cancel this round? Everyone who had not acted is notified.')) return;
            setBusy(true); setErr(null);
            try {
              await cancelVoteRound(round.id, cancelReason.trim() || undefined);
              toast('已取消本轮投票，未表态的成员已收到通知 / round cancelled — the panel was notified', { kind: 'success' });
              setCancelling(false); setCancelReason(''); refresh();
            } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
          }}>{cancelling ? '确认取消本轮 / Confirm cancel' : '取消本轮投票 / Cancel round'}</button>
          {cancelling && <button className="small" onClick={() => { setCancelling(false); setCancelReason(''); }}>返回 / Back</button>}
        </div>
      )}

      {/* V4: a finished earlier round stays reachable beneath the current one */}
      {view.last_completed && <CompletedRoundSummary v={view.last_completed} canMark={canMark} hideShort={pooledRounds > 1} onChanged={refresh} />}

      {/* V4/V4-改改: a completed round is not the end of the road — the shortlist can be re-run. */}
      {!isOpen && canManage && (
        <div className="row-wrap vote-actions" style={{ marginTop: 10 }}>
          <span className="muted small">名单要改，或有反对意见要重新过一遍？/ Shortlist changed, or an objection to re-run?</span>
          <span className="spacer" />
          <StartVoteForm pid={pid} onStarted={refresh} label="🗳 重新发起投票 / Start another round" />
        </div>
      )}
    </div>
  );
}
